import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth/context";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Alert, Badge } from "@/components/ui/Feedback";
import { TextField } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { PasswordChangeForm } from "@/features/auth/PasswordChangeForm";
import { TotpSetupPanel } from "@/features/auth/TotpSetupPanel";
import { ApiError } from "@/lib/api";
import { disableTotp } from "@/lib/auth-api";

function DisableTotpDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const mutation = useMutation({ mutationFn: () => disableTotp(password, code), onSuccess: onDone });
  let error: string | null = null;
  if (mutation.error instanceof ApiError) {
    if (mutation.error.code === "required_by_role") error = t("profile.requiredByRole");
    else if (mutation.error.code === "invalid_credentials") error = t("auth.invalidCredentials");
    else error = t("auth.invalidCode");
  }
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => !value && onClose()}
      title={t("profile.disable2faTitle")}
      description={t("profile.disable2faBody")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" type="submit" form="disable-2fa" busy={mutation.isPending}>
            {t("profile.disable2fa")}
          </Button>
        </>
      }
    >
      <form id="disable-2fa" onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <TextField
          label={t("auth.password")}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <TextField
          label={t("auth.code")}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          required
        />
      </form>
    </Dialog>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { me, refresh } = useAuth();
  const [notice, setNotice] = useState<string | null>(null);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  if (!me) return null;

  return (
    <>
      <PageHeader title={t("profile.title")} />
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card aria-labelledby="account-title">
          <CardHeader title={<span id="account-title">{t("profile.account")}</span>} />
          <CardBody>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
              <dt className="text-ink-muted">{t("admin.users.displayName")}</dt>
              <dd className="font-medium">{me.user.display_name}</dd>
              <dt className="text-ink-muted">{t("profile.username")}</dt>
              <dd>{me.user.username}</dd>
              <dt className="text-ink-muted">{t("profile.email")}</dt>
              <dd>{me.user.email ?? "—"}</dd>
              <dt className="text-ink-muted">{t("profile.roles")}</dt>
              <dd className="flex flex-wrap gap-1.5">
                {me.roles.length ? me.roles.map((r) => <Badge key={r}>{r}</Badge>) : t("profile.noRoles")}
              </dd>
            </dl>
          </CardBody>
        </Card>

        {me.viewing_as ? (
          <Alert tone="accent" className="lg:col-span-2">
            {t("viewAs.profileDisabled")}
          </Alert>
        ) : (
          <>
            <Card aria-labelledby="twofactor-title">
              <CardHeader title={<span id="twofactor-title">{t("profile.twoFactor")}</span>} />
              <CardBody className="space-y-4">
                {notice && <Alert tone="primary">{notice}</Alert>}
                <p className="text-sm text-ink-muted">
                  {me.user.totp_enabled ? t("profile.twoFactorOn") : t("profile.twoFactorOff")}
                </p>
                {me.user.totp_enabled ? (
                  <Button variant="danger" onClick={() => setDisabling(true)}>
                    {t("profile.disable2fa")}
                  </Button>
                ) : (
                  <Button onClick={() => setEnabling(true)}>{t("profile.enable2fa")}</Button>
                )}
              </CardBody>
            </Card>

            <Card aria-labelledby="password-title" className="lg:col-span-2">
              <CardHeader title={<span id="password-title">{t("auth.changePassword")}</span>} />
              <CardBody className="max-w-md">
                <PasswordChangeForm
                  minLength={me.password_min_length}
                  onDone={() => setNotice(t("profile.passwordChanged"))}
                />
              </CardBody>
            </Card>
          </>
        )}
      </div>

      <Dialog
        open={enabling}
        onOpenChange={setEnabling}
        title={t("auth.setupTitle")}
        description={t("auth.setupBody")}
      >
        {enabling && (
          <TotpSetupPanel
            onDone={() => {
              setEnabling(false);
              setNotice(t("profile.enabled2fa"));
              void refresh();
            }}
          />
        )}
      </Dialog>
      <DisableTotpDialog
        open={disabling}
        onClose={() => setDisabling(false)}
        onDone={() => {
          setDisabling(false);
          setNotice(t("profile.disabled2fa"));
          void refresh();
        }}
      />
    </>
  );
}
